// @ts-strict-ignore
import { Injectable, NgZone } from '@angular/core';
import { SohoMessageService, SohoModalDialogRef, SohoModalDialogService } from 'ids-enterprise-ng';
import { Observable, Subject, BehaviorSubject } from 'rxjs';

import { LMLoggerService } from '@services/lm-logger.service';
import { LMActionFormDialogComponent } from './lm-action-form-dialog.component';
import { LMBusyIndicatorService } from '@services/lm-busyindicator.service';
import { LMFormatUtilService } from '@services/utils/lm-format-util.service';
import { LMDataService } from '@services/lm-data.service';
import { LMActionRequest } from '@services/lm-action.service';
import { LMTransactionState } from '@transaction/lm-transaction-state';
import { LMScheduleDialogService } from '@modal/print-dialog/schedule/lm-schedule-dialog.service';
import { AppConstants, LMActionRequestSaveMode, LMActionRequestStatus, LMUniqueIdService } from '@utils/index';
import { LMDialogButtonKey } from '@utils/app.constants';
import { filter } from 'rxjs/operators';
import { LMContextService } from '@services/lm-context.service';
import { LMColorId, LMThemeService } from '@banner/lm-theme.service';

export enum LMActionFormDialogResultCode {
  ACTION_FORM_DIALOG_CANCEL = 0,
  ACTION_FORM_DIALOG_OK,
  ACTION_FORM_DIALOG_OK2,
  ACTION_FORM_DIALOG_COMPARE,
  ACTION_FORM_DIALOG_PFACTION,
  ACTION_FORM_DIALOG_SAVED,
  ACTION_FORM_DIALOG_ENABLEBUTTONS,
  ACTION_FORM_DIALOG_OPENERROR,
  ACTION_FORM_DIALOG_CLEAR,
  ACTION_FORM_VALIDATE_ONLY
}

export interface LMActionFormDialogResult {
  result: LMActionFormDialogResultCode;
  pfActionName?: string;
  pfActionRequestSaveMode?: LMActionRequestSaveMode;
  scheduleView?: LMDataView;
  status?: string;
  actionRequestArray: LMActionRequest[];
  transactionStateResult: LMTransactionState;
  openError?: LMError;
}

@Injectable()
export class LMActionFormDialogService {
  constructor(
    private ngZone: NgZone,
    private sohoMessageService: SohoMessageService,
    private sohoModalDialogService: SohoModalDialogService,
    private lmBusyIndicatorService: LMBusyIndicatorService,
    private lmContextService: LMContextService,
    private lmDataService: LMDataService,
    private lmFormatUtilService: LMFormatUtilService,
    private lmLoggerService: LMLoggerService,
    private lmScheduleDialogService: LMScheduleDialogService,
    private lmThemeService: LMThemeService,
    private lmUniqueIdService: LMUniqueIdService
  ) {}

  private openDialogs: Array<SohoModalDialogRef<LMActionFormDialogComponent>> = [];
  private scheduleView: Array<LMDataView> = [];

  public get hasOpenDialog(): boolean {
    return this.openDialogs.length > 0;
  }

  public get getOpenDialog(): SohoModalDialogRef<LMActionFormDialogComponent> {
    return this.hasOpenDialog ? this.openDialogs[ this.openDialogs.length - 1 ] : undefined;
  }

  public getActionFormDialogDataView(formDataUrl: string): Observable<LMDataView> {
    return this.lmDataService.getRecord(formDataUrl);
  }

  public getDataViewFieldButtonDataView(formDataUrl: string, dataView?: LMDataView): Observable<LMDataView> {
    if (dataView) {
      const formDataView: LMDataView = JSON.parse(JSON.stringify(dataView));
      formDataView.status = AppConstants.STATUS_COMPLETED;
      return new Observable<LMDataView>(observer => {
        observer.next(formDataView);
        observer.complete();
      });
    }
    return this.lmDataService.getRecord(formDataUrl);
  }

  public runActionFormDialog(
    formUrlInfo: LMFormUrlInfo,
    formSpec: LMFormSpec,
    actionSpec: LMActionSpec,
    dataView?: LMDataView,
    transactionState?: LMTransactionState,
    actionFormOptions?: LMActionFormOptions,
    loadTimerStarted?: boolean
  ): Observable<LMActionFormDialogResult> {
    this.lmBusyIndicatorService.showBusyIndicator();

    this.lmLoggerService.log([ 'LMActionFormDialog', 'formService.getFormSpec()', 'Complete' ]);

    // actionDialog$ subject is there to facilitate communication between the form component and dialog frame.
    const actionDialog$: Subject<LMActionFormDialogResult> = new Subject<LMActionFormDialogResult>();
    // Action form dialog pushes the result onAfterClose and the transaction service observer will get the result.
    // do not have to push the transaction observer here
    // actionDialog$.observers.push(observer);

    let dialog: SohoModalDialogRef<LMActionFormDialogComponent> = this.sohoModalDialogService.modal(
      LMActionFormDialogComponent, null, { fullsize: 'responsive' });

    // Todo: Button text, title translation.
    const buttons = this.getDialogButtons(dataView, formSpec, actionSpec, dialog, actionDialog$, transactionState, actionFormOptions);

    const defaultButton = buttons.find(button => button.isDefault);

    if (!loadTimerStarted) {
      this.lmLoggerService.time({
        id: 'TotalActionForm',
        description: 'TOTAL ACTION FORM',
        className: 'LMActionFormDialogService',
        timingStart: 'LMActionFormDialogService -> runActionFormDialog()',
        timingEnd: 'LMFormContentComponent -> ngAfterViewInit()'
      });
    }

    const automationId = `lm-${formSpec?.automationId}-modal`;
    const attributes: SohoAttributesArray = [ {
      name: 'data-automation-id',
      value: automationId
    } ];

    const useFullPage = (formSpec?.busFormSpec?.panels?.length > 1)? 'always' : false;

    const sohoModalOptions: SohoModalOptions = {
      //id:         automationId, //Note: setting id here breaks QA tests
      title:      this.getDialogTitle(formSpec.busFormSpec.title, dataView, actionSpec, formSpec, transactionState),
      cssClass:   this.getCSSClass(formSpec),
      isAlert:    false,
      fullsize:   useFullPage,
      attributes,
      buttons,
    };

    let alertMessage: string;

    if (actionSpec && transactionState && transactionState.transactionRequestList && transactionState.transactionRequestList.length > 1) {
      const count = transactionState.transactionRequestList.length;
      const phrase = actionSpec.type === AppConstants.ACTION_TYPE_DELETE ? 'DeleteConfirmationAlertMessage' :
        (transactionState?.busListSpec?.attachmentListInfo) ? 'AttachmentConfirmationAlertMessage' : 'ActionConfirmationAlertMessage';
      alertMessage = this.lmFormatUtilService.formatMessage(Soho.Locale.translate(phrase), count);
    }

    return new Observable<LMActionFormDialogResult>(observer => {
      dialog
        .options(sohoModalOptions)
        // .title(this.getDialogTitle(formSpec.busFormSpec.title, dataView, actionSpec, formSpec, transactionState))
        // .isAlert(false)
        .open()
        .beforeOpen((e) => {
          const elementNodeList = document.querySelectorAll('.lm-action-form-dialog.lm-themed-modal .modal-header');
          if (elementNodeList.length === 0) {
            return true;
          }
          const classList = elementNodeList[0].classList;
          classList?.add('is-personalizable', 'header');
          this.lmBusyIndicatorService.hideBusyIndicator();
          return true;
        })
        .apply((actionForm: LMActionFormDialogComponent) => {
          actionForm.formUrlInfo = formUrlInfo;
          actionForm.formSpec = formSpec;
          actionForm.dataView = dataView;
          actionForm.alertMessage = alertMessage;
          actionForm.formOptions = {
            actionFormDialogData: {
              actionSpec,
              actionDialog$
            }
          };
          actionForm.actionFormOptions = actionFormOptions;
          actionForm.defaultButton = defaultButton;
          // TODO: Set the title after processing message rules
          // dialog.title(formSpec.busFormSpec.title);
          // actionForm.titleChanged.first().subscribe(newTitle => {
          //   this.dialog.title(newTitle);
          //   actionForm.triggerDetectChanges();
          // });

          // Need to trigger change detection manually for Change Password dialog, revisit this in the future if there is an alternative way
          // actionForm.triggerDetectChanges();
        })
        .beforeClose(() => {
          // if ref.dialogResult is undefined, the escape key was used
          const isEscapeKey = (!dialog.dialogResult);
          // TODO: restore the check back when beforeClose() is fixed
          // dataView indicates a Cancel key is on the dialog; ref.dialogResult.result is the button used
          if (dataView && (isEscapeKey/* || ref.dialogResult.result === LMActionFormDialogResultCode.ACTION_FORM_DIALOG_CANCEL*/)) {
            const model = dialog.componentDialog.formComponent.model;
            if (model.isDirty) {
              // TODO: would be nice to use dialog.componentDialog.formComponent.displayUnsavedChanges() here
              // TODO: restore when beforeClose() is fixed
              // this.displayUnsavedChanges().then((allowNavigation: boolean) => {
              //   return allowNavigation;
              // });
              return false;
            }
          }
          // The cleanup is done earlier in before close since after close will be triggered after 20ms delay.
          // sohoxi waits for the overlay to be removed before triggering after close. LMCLIENT-29718
          this.openDialogs.pop();
          this.scheduleView.pop();
          actionDialog$.complete(); // UnCommented this line out due to multiple toast messages displaying.
          // Still need to figure out issue on check control not being unchecked after doing a Cancel - LMCLIENT-25672
          return true;
        })
        .afterClose(result => {
          dialog = null;

          // if we don't have a result, then create a cancel
          if (!result) {
            result = {
              result: LMActionFormDialogResultCode.ACTION_FORM_DIALOG_CANCEL
            } as LMActionFormDialogResult;
          }

          observer.next(result);
          observer.complete();
          // actionDialog$.complete(); // Commented this line out due to multiple toast messages displaying.
          // Still need to figure out issue on check control not being unchecked after doing a Cancel - LMCLIENT-25672
        })
        .afterOpen(() => {
          this.openDialogs.push(dialog);
          this.scheduleView.push(<LMDataView>{});
        });
    });
  }

  private getCSSClass(formSpec: LMFormSpec): string {
    const cssClass = [ 'lm-action-form-dialog' ];

    if (this.lmThemeService.getCurrentColorId() !== LMColorId.default) {
      cssClass.push('lm-themed-modal');
    }

    if (formSpec?.busFormSpec?.panels?.length > 1) {
      cssClass.push('lm-has-composite');
      cssClass.push('lm-full-tabbed-action-form');
    }
    return cssClass.join(' ');
  }

  private getDialogTitle(titleMsgRule: LMMessageRule, dataView: LMDataView, actionSpec: LMActionSpec, formspec: LMFormSpec, transactionState?: LMTransactionState): string {
    const title = titleMsgRule && dataView ? this.lmFormatUtilService.formatMessageRule(titleMsgRule, dataView, formspec.busFormSpec.fields) : Soho.Locale.translate('ActionForm');

    return title;
  }

  // TODO: consider removing this method and just use just dialog.close if all other stuff below will no longer be needed
  private dialogClose(dialog: SohoModalDialogRef<LMActionFormDialogComponent>, actionDialog$: Subject<LMActionFormDialogResult>, result: LMActionFormDialogResult) {
    // when a dialog close is fired bu deactivation guard, the cleanup is not done.
    // So moving the cleanup code to before or after close
    // actionDialog$.complete(); // UnCommented this line out due to multiple toast messages displaying.
    //                           // Still need to figure out issue on check control not being unchecked after doing a Cancel - LMCLIENT-25672
    // this.openDialogs.pop();
    // this.scheduleView.pop();
    dialog.close(result);
  }

  // TODO: Badly needs cleanup and make sure button click functions runs in ng zone unless they only do dialog.close()
  private getDialogButtons(dataView: LMDataView, formSpec: LMFormSpec, actionSpec: LMActionSpec, dialog: SohoModalDialogRef<LMActionFormDialogComponent>,
    actionDialog$: Subject<LMActionFormDialogResult>, transactionState?: LMTransactionState, actionFormOptions?: LMActionFormOptions): SohoModalButton[] {
    const actionRequestArray = (transactionState) ? transactionState.transactionRequestList : undefined;
    let renderAllPfActions = true;
    const current = (Soho as any).breakpoints.current();
    if (current === 'phone' || current === 'phonedown') {
      // Render cancel, approve and reject only in mobile for process flow action requests.
      // Other action dialog forms will have cancel and submit
      renderAllPfActions = (dataView.processFlowActions && dataView.processFlowActions.length > 0) ? false : true;
    }

    const formNamePrefix = (formSpec.busFormSpec.namePrefix) ? formSpec.busFormSpec.namePrefix : '';
    const formName = formSpec.busFormSpec.name;
    let buttons: SohoModalButton[] = [];

    // if there is no action just present a close button
    // this happens when a list opens a record in
    // read only mode (no valid update action)

   if (!actionSpec || actionFormOptions?.hideActionButtons) {
      buttons.push({
        id:    this.lmUniqueIdService.uniqueId([ formNamePrefix, formName, LMDialogButtonKey.CLOSE ]),
        text:  Soho.Locale.translate(LMDialogButtonKey.CLOSE),
        click: () => {

          this.dialogClose(dialog, actionDialog$, <LMActionFormDialogResult>{
            result: LMActionFormDialogResultCode.ACTION_FORM_DIALOG_CANCEL
          });
        }
      });

      return buttons;
    }

    if (actionFormOptions?.isDataViewFieldButtonForm) {
      return this.getDataViewFormButtons(formSpec, dialog, actionDialog$, transactionState, actionFormOptions);
    }

    if (dataView) {
      // 1. Cancel Button
      buttons.push({
        id: this.lmUniqueIdService.uniqueId([ formNamePrefix, formName, LMDialogButtonKey.CANCEL ]),
        text: Soho.Locale.translate(LMDialogButtonKey.CANCEL),
        click: () => {
          // TODO: would be nice to use dialog.componentDialog.formComponent.displayUnsavedChanges() here
          // but the message does not always display
          const model = dialog.componentDialog.formComponent.model;
          if (model.isDirty) {
            this.displayUnsavedChanges().then((allowNavigation: boolean) => {
              if (allowNavigation) {
                this.dialogClose(dialog, actionDialog$, <LMActionFormDialogResult>{
                  result: LMActionFormDialogResultCode.ACTION_FORM_DIALOG_CANCEL
                });
              }
            });
          } else {
            this.dialogClose(dialog, actionDialog$, <LMActionFormDialogResult>{
              result: LMActionFormDialogResultCode.ACTION_FORM_DIALOG_CANCEL
            });
          }
        }
      });

            // 2. Compare button
      if (dataView.canCompare && renderAllPfActions) {
        buttons.push({
          id: this.lmUniqueIdService.uniqueId([ formNamePrefix, formName, LMDialogButtonKey.COMPARE ]),
          text: Soho.Locale.translate(LMDialogButtonKey.COMPARE),
          click: (event) => {
            this.subscribeToActionDialogButtonClick(dialog, actionDialog$);
            actionDialog$.next(<LMActionFormDialogResult>{
              result: LMActionFormDialogResultCode.ACTION_FORM_DIALOG_COMPARE
            });
          }
        });
      }

      // 3. Process flow actions
      if (dataView.processFlowActions && dataView.processFlowActions.length > 0 && !this.lmContextService.isDataModificationNotAllowed()) {
        // On a phone, render approve or reject if available. If not, pick max 2 buttons to be rendered.
        const pfActions = (renderAllPfActions) ? dataView.processFlowActions : this.getPhonePFActions(dataView.processFlowActions);

        // eslint-disable-next-line @typescript-eslint/prefer-for-of
        for (let i = 0; i < pfActions.length; i++) {
          const pfAction = pfActions[i];
          buttons.push({
            id: this.lmUniqueIdService.uniqueId([ formNamePrefix, formName, pfAction.label ]),
            text: pfAction.label,
            click: (event) => {
              this.subscribeToActionDialogButtonClick(dialog, actionDialog$);
              actionDialog$.next(<LMActionFormDialogResult>{
                result: LMActionFormDialogResultCode.ACTION_FORM_DIALOG_PFACTION,
                pfActionName: pfAction.name
              });
            }
          });
        }
      }

      if (actionSpec.willRunAsynchronous && !this.lmContextService.isDataModificationNotAllowed()) {
        buttons.push({
          id: this.lmUniqueIdService.uniqueId([ formNamePrefix, formName, LMDialogButtonKey.SCHEDULE ]),
          text: Soho.Locale.translate(LMDialogButtonKey.SCHEDULE),
          click: (event) => {
            this.lmScheduleDialogService.openScheduleDialog(dataView.busClassName, actionSpec.name, actionDialog$)
              .subscribe((scheduleView: LMDataView) => {
                if (scheduleView) {
                  this.scheduleView[ this.scheduleView.length - 1 ] = scheduleView;
                }
              });
            this.subscribeToActionDialogButtonClick(dialog, actionDialog$);
          }
        });
      }

      // 4. Ok Button (Save as draft)

      const hideSubmit = actionFormOptions?.hideSubmitButton || dataView.displayOnly || this.lmContextService.isDataModificationNotAllowed() || 
        !renderAllPfActions;
          
      if (!hideSubmit) {
        let okText = LMDialogButtonKey.SUBMIT;
        let okText2;
        let pfActionRequestSaveMode: LMActionRequestSaveMode;
        let pfActionRequestSaveMode2: LMActionRequestSaveMode;
        const defaultButton = true; // (dataView.processFlowActions && dataView.processFlowActions.length > 0) ? false : true;

        const isInitiator = dataView.contextProperties && dataView.contextProperties['context_prd_initiator'] !== undefined && dataView.contextProperties['context_prd_initiator'] === 'true';
        const isActionRequest = dataView.contextProperties && dataView.contextProperties['context_prd_action_prd_request'] !== undefined
          && dataView.contextProperties['context_prd_action_prd_request'].length > 0;
        const isApprover = dataView.contextProperties && dataView.contextProperties['context_prd_approver'] !== undefined && dataView.contextProperties['context_prd_approver'] === 'true';
        const isFinalApprover = dataView.contextProperties && dataView.contextProperties['context_prd_finalapprover'] !== undefined
          && dataView.contextProperties['context_prd_finalapprover'] === 'true';

        if (isInitiator) {
          if (isActionRequest) {
            // Action request is already created.
            okText = LMDialogButtonKey.SAVEASDRAFT;
            pfActionRequestSaveMode = LMActionRequestSaveMode.DRAFT;
            const notInProcess = (dataView.contextProperties['context_prd_action_prd_request_prd_status']
              && dataView.contextProperties['context_prd_action_prd_request_prd_status'] === LMActionRequestStatus.NOTINPROCESS);
            if (notInProcess) {
              okText2 = LMDialogButtonKey.SUBMIT;
              pfActionRequestSaveMode2 = LMActionRequestSaveMode.SUBMIT;
            }
          } else {
            okText = LMDialogButtonKey.SUBMIT;
            pfActionRequestSaveMode = LMActionRequestSaveMode.SUBMIT;
            okText2 = LMDialogButtonKey.SAVEASDRAFT;
            pfActionRequestSaveMode2 = LMActionRequestSaveMode.DRAFT;
          }
        } else if (isApprover || isFinalApprover) {
          okText = LMDialogButtonKey.SAVEASDRAFT;
          pfActionRequestSaveMode = LMActionRequestSaveMode.DRAFT;
        }

        if (okText2) {
          buttons.push({
            id: this.lmUniqueIdService.uniqueId([ formNamePrefix, formName, okText2 ]),
            text: Soho.Locale.translate(okText2),
            validate: false,
            click: (event) => {
              this.ngZone.run(() => {
                this.subscribeToActionDialogButtonClick(dialog, actionDialog$);
                const scheduleDataView = this.scheduleView.length ? this.scheduleView[this.scheduleView.length - 1] : null;
                const scheduleView = (scheduleDataView && scheduleDataView.fields) ? scheduleDataView : null;

                actionDialog$.next(<LMActionFormDialogResult>{
                  result: LMActionFormDialogResultCode.ACTION_FORM_DIALOG_OK2,
                  pfActionRequestSaveMode: pfActionRequestSaveMode2,
                  actionRequestArray,
                  transactionStateResult: transactionState,
                  scheduleView
                });
              });
            }
          });
        }

        if (okText) {
          let submitLabel = Soho.Locale.translate(okText);
          if (okText === LMDialogButtonKey.SUBMIT && formSpec.busFormSpec.submitLabel) {
            submitLabel = this.lmFormatUtilService.formatMessageRule(formSpec.busFormSpec.submitLabel, dataView);
          }
          buttons.push({
            id: this.lmUniqueIdService.uniqueId([ formNamePrefix, formName, submitLabel ]),
            text: submitLabel,
            isDefault: defaultButton,
            validate: false,
            cssClass: ' no-validation ',
            click: (event) => {
              this.ngZone.run(() => {
                this.subscribeToActionDialogButtonClick(dialog, actionDialog$);
                const scheduleDataView = this.scheduleView.length ? this.scheduleView[this.scheduleView.length - 1] : null;
                const scheduleView = (scheduleDataView && scheduleDataView.fields) ? scheduleDataView : null;

                actionDialog$.next(<LMActionFormDialogResult>{
                  result: LMActionFormDialogResultCode.ACTION_FORM_DIALOG_OK,
                  pfActionRequestSaveMode,
                  actionRequestArray,
                  transactionStateResult: transactionState,
                  scheduleView
                });
              });
            }
          });
        }
      }
    } else {
      let submitLabel = Soho.Locale.translate(LMDialogButtonKey.SUBMIT);
      if (formSpec.busFormSpec.submitLabel) {
        submitLabel = this.lmFormatUtilService.formatMessageRule(formSpec.busFormSpec.submitLabel, dataView);
      }
      buttons = [
        {
          id:    this.lmUniqueIdService.uniqueId([ formNamePrefix, formName, LMDialogButtonKey.CANCEL ]),
          text:  Soho.Locale.translate(LMDialogButtonKey.CANCEL),
          click: () => {
            this.dialogClose(dialog, actionDialog$, <LMActionFormDialogResult>{
              result: LMActionFormDialogResultCode.ACTION_FORM_DIALOG_CANCEL
            });
          }
        },
        {
          id:        this.lmUniqueIdService.uniqueId([ formNamePrefix, formName, submitLabel ]),
          text:      submitLabel,
          isDefault: true,
          validate:  false,
          click:     () => {
            actionDialog$.next(<LMActionFormDialogResult>{
              result: LMActionFormDialogResultCode.ACTION_FORM_DIALOG_OK
            });
            actionDialog$.subscribe((result: LMActionFormDialogResult) => {
              if (result.result === LMActionFormDialogResultCode.ACTION_FORM_DIALOG_SAVED) {
                this.dialogClose(dialog, actionDialog$, result);
              }
            });
          }
        }
      ];
    }
    return buttons;
  }

  private getPhonePFActions(pfActions: LMProcessFlowAction[]): LMProcessFlowAction[] {
    // On a phone, render approve or reject if available. If not, pick max 2 buttons to be rendered.
    const phonePFActions = [];
    const approveIndex = pfActions.findIndex(pfAction => pfAction.name.toLowerCase() === 'approve');
    if (approveIndex !== -1) {
      phonePFActions[phonePFActions.length] = pfActions[approveIndex];
    }
    let rejectIndex = pfActions.findIndex(pfAction => pfAction.name.toLowerCase() === 'reject');
    let rejectActionName = 'reject';
    if (rejectIndex !== -1) {
      phonePFActions[phonePFActions.length] = pfActions[rejectIndex];
    } else {
      rejectIndex = pfActions.findIndex(pfAction => pfAction.name.toLowerCase() === 'deny');
      if (rejectIndex !== -1) {
        rejectActionName = 'deny';
        phonePFActions[phonePFActions.length] = pfActions[rejectIndex];
      }
    }

    // The names of the buttons are customized in the flow.
    if (phonePFActions.length === 0) {
      // Get the first 2 pfActions to be rendered in a phone
      for (let i = 0; i < pfActions.length && i < 2; i++) {
        phonePFActions[i] = pfActions[i];
      }
    } else if (phonePFActions.length === 1 && pfActions.length > 1) {
      if (approveIndex === -1) {
        // Reject or deny button is there in the collection.
        phonePFActions[phonePFActions.length] = pfActions.find(pfAction => pfAction.name.toLowerCase() !== rejectActionName);
      } else {
        // Approve button is there in the collection.
        phonePFActions[phonePFActions.length] = pfActions.find(pfAction => pfAction.name.toLowerCase() !== 'approve');
      }
    }
    return phonePFActions;
  }

  private subscribeToActionDialogButtonClick(dialog: SohoModalDialogRef<LMActionFormDialogComponent>, actionDialog$: Subject<LMActionFormDialogResult>) {
    this.disableButtons(dialog);
    // With first(), an error is thrown when subject completes (Emptyerror no elements in sequence).
    // Cannot use take(1) because the message intended to go to the form also comes here.
    // Do not cancel the subscription when the result is OK, OK2, PFACTION, COMPARE. Wait for saved or enablebuttons.
    const subscription = actionDialog$.subscribe((result: LMActionFormDialogResult) => {
      if (result.result === LMActionFormDialogResultCode.ACTION_FORM_DIALOG_SAVED) {
        this.dialogClose(dialog, actionDialog$, result);
        subscription.unsubscribe();
      } else if (result.result === LMActionFormDialogResultCode.ACTION_FORM_DIALOG_ENABLEBUTTONS) {
        this.enableButtons(dialog);
        subscription.unsubscribe();
      }
    });
  }

  /* tslint:disable-next-line:max-line-length */
  private getDataViewFormButtons(formSpec: LMFormSpec, dialog: SohoModalDialogRef<LMActionFormDialogComponent>, actionDialog$: Subject<LMActionFormDialogResult>, transactionState?: LMTransactionState, actionFormOptions?: LMActionFormOptions): SohoModalButton[] {
    const formNamePrefix = (formSpec.busFormSpec.namePrefix) ? formSpec.busFormSpec.namePrefix : '';
    const formName = formSpec.busFormSpec.name;
    const actionRequestArray = (transactionState) ? transactionState.transactionRequestList : undefined;
    const buttons: SohoModalButton[] = [];

    // Buttons for the job parameters form dialog
    // 1. Cancel Button
    buttons.push({
      id: this.lmUniqueIdService.uniqueId([ formNamePrefix, formName, LMDialogButtonKey.CANCEL ]),
      text: Soho.Locale.translate(LMDialogButtonKey.CANCEL),
      click: () => {
        const model = dialog.componentDialog.formComponent.model;
        if (model.isDirty) {
          this.displayUnsavedChanges().then((allowNavigation: boolean) => {
            if (allowNavigation) {
              this.dialogClose(dialog, actionDialog$, <LMActionFormDialogResult>{
                result: LMActionFormDialogResultCode.ACTION_FORM_DIALOG_CANCEL
              });
            }
          });
        } else {
          this.dialogClose(dialog, actionDialog$, <LMActionFormDialogResult>{
            result: LMActionFormDialogResultCode.ACTION_FORM_DIALOG_CANCEL
          });
        }
      }
    });

    // 2. Clear button
    buttons.push({
      id: this.lmUniqueIdService.uniqueId([ formNamePrefix, formName, LMDialogButtonKey.CLEAR ]),
      text: Soho.Locale.translate(LMDialogButtonKey.CLEAR),
      click: (event) => {
        const model = dialog.componentDialog.formComponent.model;
        if (model.isDirty || actionFormOptions?.dataViewFieldButtonHasData) {
          const message = '<p>' + Soho.Locale.translate('DocumentHasChangesMessage') + '</p>'
            + '<p>' + Soho.Locale.translate('ClickStayToSaveChanges') + '</p>'
            + '<p>' + Soho.Locale.translate('ClickLeaveToClearForm') + '</p>';
          this.displayUnsavedChanges(message).then((allowNavigation: boolean) => {
            if (allowNavigation) {
              this.subscribeToDataViewFormButtonClick(dialog, actionDialog$);
              actionDialog$.next(<LMActionFormDialogResult>{
                result: LMActionFormDialogResultCode.ACTION_FORM_DIALOG_CLEAR,
                actionRequestArray,
                transactionStateResult: transactionState,
              });
            } else {
              this.enableButtons(dialog);
            }
          });
        } else {
          this.dialogClose(dialog, actionDialog$, <LMActionFormDialogResult>{
            result: LMActionFormDialogResultCode.ACTION_FORM_DIALOG_CLEAR
          });
        }
      }
    });

    // 3. Ok button
    buttons.push({
      id: this.lmUniqueIdService.uniqueId([ formNamePrefix, formName, LMDialogButtonKey.OK ]),
      text: Soho.Locale.translate(LMDialogButtonKey.OK),
      isDefault: true,
      validate: false,
      click: (event) => {
        this.subscribeToDataViewFormButtonClick(dialog, actionDialog$);
        actionDialog$.next(<LMActionFormDialogResult>{
          result: LMActionFormDialogResultCode.ACTION_FORM_VALIDATE_ONLY,
          actionRequestArray,
          transactionStateResult: transactionState,
        });
      }
    });

    return buttons;
  }

  private subscribeToDataViewFormButtonClick(dialog: SohoModalDialogRef<LMActionFormDialogComponent>, actionDialog$: Subject<LMActionFormDialogResult>) {
    this.disableButtons(dialog);
    // With first(), an error is thrown when subject completes (Emptyerror no elements in sequence).
    // Cannot use take(1) because the message intended to go to the form also comes here.
    // Cancel the subscription on a save, clear or enablebuttons.
    const subscription = actionDialog$.subscribe((result: LMActionFormDialogResult) => {
      if (result.result === LMActionFormDialogResultCode.ACTION_FORM_DIALOG_SAVED
        || result.result === LMActionFormDialogResultCode.ACTION_FORM_DIALOG_CLEAR) {
        this.dialogClose(dialog, actionDialog$, result);
        subscription.unsubscribe();
      } else if (result.result === LMActionFormDialogResultCode.ACTION_FORM_DIALOG_ENABLEBUTTONS) {
        this.enableButtons(dialog);
        subscription.unsubscribe();
      }
    });
  }

  // TODO: would be nice to use dialog.componentDialog.formComponent.displayUnsavedChanges() but the message does not always display
  private displayUnsavedChanges(message?: string): Promise<any> {
    const youMayNavigateSource = new BehaviorSubject<boolean>(false);
    const youMayNavigate: Observable<boolean> = youMayNavigateSource.asObservable();

    const dialogId = 'UnsavedChanges';
    const buttons = [
      {
        id:    this.lmUniqueIdService.uniqueId([ dialogId, LMDialogButtonKey.NO ]),
        text:  Soho.Locale.translate(LMDialogButtonKey.NO),
        click: (e, modal) => {
          youMayNavigateSource.next(false);
          youMayNavigateSource.complete();
          modal.close();
        },
      },
      {
        id:    this.lmUniqueIdService.uniqueId([ dialogId, LMDialogButtonKey.YES ]),
        text:  Soho.Locale.translate(LMDialogButtonKey.YES),
        click: (e, modal) => {
          youMayNavigateSource.next(true);
          youMayNavigateSource.complete();
          modal.close();
        },
      }
    ];

    const title = Soho.Locale.translate('UnsavedChangesMessage');
    if (!message) {
      message = '<p>' + Soho.Locale.translate('DocumentHasChangesMessage') + '</p>'
        + '<p>' + Soho.Locale.translate('ClickStayToSaveChanges') + '</p>'
        + '<p>' + Soho.Locale.translate('ClickLeaveToIgnoreChanges') + '</p>';
    }

    const attributes: SohoAttributesArray = [ {
      name: 'data-automation-id',
      value: `lm-discard-changes-modal`
    } ];

    const options: SohoMessageOptions = {
      attributes
    };

    this.sohoMessageService
      .message(options)
      .message(Soho.Locale.translate('DiscardChangesMsg'))
      .allowedTags('<p>')
      .title('')
      .buttons(buttons)
      .open();

    return (youMayNavigate.pipe(filter(el => el))).toPromise();
  }

  private disableButtons(dialog: SohoModalDialogRef<LMActionFormDialogComponent>) {
    const parent = dialog?.buttonsetAPI?.element;
    const children = parent?.children;
    for (let index = 0, len = children?.length; index < len; index++) {
      const btn = children[ index ];
      const id = btn.getAttribute('id');
      if (id.indexOf(LMDialogButtonKey.CLOSE) > -1) {
        continue;
      }
      btn.setAttribute('disabled', 'true');
    }
  }

  private enableButtons(dialog: SohoModalDialogRef<LMActionFormDialogComponent>) {
    const parent = dialog?.buttonsetAPI?.element;
    const children = parent?.children;
    for (let index = 0, len = children?.length; index < len; index++) {
      const btn = children[ index ];
      const id = btn.getAttribute('id');
      if (id.indexOf(LMDialogButtonKey.CLOSE) > -1) {
        continue;
      }
      btn.removeAttribute('disabled');
    }
  }
}
